import {
	check,
	boolean,
	decimal,
	index,
	integer,
	jsonb,
	pgEnum,
	pgTable,
	text,
	unique,
	uniqueIndex,
	uuid
} from "drizzle-orm/pg-core";
import { sql } from "drizzle-orm";

import { baseTableProps } from "../../baseTableProps";
import { addresses } from "../addresses/addresses.model";
import { financialReviewTemplates } from "../company-financials/company-financials.model";
import { contacts } from "../contacts/contacts.model";
import type { DisplayNameMeta } from "../_shared/display-name";

export const companyReviewCadenceEnum = pgEnum("company_review_cadence", ["monthly", "quarterly"]);

export const companies = pgTable(
	"companies",
	{
		...baseTableProps,
		legalName: text("legal_name").notNull(),
		displayName: text("display_name")
			.notNull()
			.$default(() => sql`legal_name`),
		displayNameMeta: jsonb("display_name_meta").$type<DisplayNameMeta>(),
		enabledFor: text("enabled_for")
			.array()
			.notNull()
			.default(sql`'{}'::text[]`),
		email: text("email"),
		phone: text("phone"),
		website: text("website"),
		offeringType: text("offering_type"),
		financialReviewEnabled: boolean("financial_review_enabled").notNull().default(false),
		reviewCadence: companyReviewCadenceEnum("review_cadence").notNull().default("monthly"),
		reviewTemplateId: uuid("review_template_id").references(() => financialReviewTemplates.id, {
			onDelete: "set null"
		}),
		submissionTargetDay: integer("submission_target_day").notNull().default(10)
	},
	(table) => [
		check(
			"companies_submission_target_day_check",
			sql`${table.submissionTargetDay} >= 1 AND ${table.submissionTargetDay} <= 31`
		)
	]
);

/** Company to addresses hub join. Additive: a company may have zero or many hub addresses. */
export const xCompanyAddresses = pgTable(
	"x_company_addresses",
	{
		...baseTableProps,
		companyId: uuid("company_id")
			.references(() => companies.id, { onDelete: "cascade" })
			.notNull(),
		addressId: uuid("address_id")
			.references(() => addresses.id, { onDelete: "cascade" })
			.notNull()
	},
	(table) => [
		index("x_company_addresses_company_id_idx").on(table.companyId),
		index("x_company_addresses_address_id_idx").on(table.addressId)
	]
);

export const companyPartyRoleEnum = pgEnum("company_party_role", ["owner"]);

export const companyOwnershipClasses = pgTable(
	"company_ownership_classes",
	{
		...baseTableProps,
		companyId: uuid("company_id")
			.references(() => companies.id, { onDelete: "cascade" })
			.notNull(),
		interestType: text("interest_type").notNull(),
		label: text("label"),
		displayName: text("display_name")
	},
	(table) => [
		unique("company_ownership_classes_identity_uq")
			.on(table.companyId, table.interestType, table.label)
			.nullsNotDistinct()
	]
);

/** Current-state ownership cap table. A row points from an owned company to one owner party. */
export const companyParties = pgTable(
	"company_parties",
	{
		...baseTableProps,
		companyId: uuid("company_id")
			.references(() => companies.id, { onDelete: "cascade" })
			.notNull(),
		role: companyPartyRoleEnum("role").notNull(),
		partyCompanyId: uuid("party_company_id").references(() => companies.id, {
			onDelete: "no action"
		}),
		partyContactId: uuid("party_contact_id").references(() => contacts.id, {
			onDelete: "no action"
		}),
		ownershipClassId: uuid("ownership_class_id").references(() => companyOwnershipClasses.id, {
			onDelete: "restrict"
		}),
		// 8dp, not 2dp: a single 0.01-point rounding step moves a holder by up to 0.005
		// absolute, which below roughly a 0.05% stake exceeds a 10% relative hit, and
		// forcing the complete direct-owner set to sum to 100 means dumping the remainder
		// onto one row. Percentages are entered from the raise platform, which is the
		// system of record for how they are derived; this platform only stores them.
		// See ../investments/ADR.md ADR-10 and ADR-11.
		ownershipPercentage: decimal("ownership_percentage", {
			precision: 11,
			scale: 8,
			mode: "number"
		})
			.notNull()
			.default(100),
		shareClass: text("share_class")
	},
	(table) => [
		check(
			"company_party_xor_check",
			sql`(${table.partyCompanyId} IS NULL) <> (${table.partyContactId} IS NULL)`
		),
		check(
			"company_party_no_self_ownership_check",
			sql`(${table.partyCompanyId} IS NULL) OR (${table.partyCompanyId} <> ${table.companyId})`
		),
		check(
			"company_party_ownership_percentage_check",
			sql`${table.ownershipPercentage} > 0 AND ${table.ownershipPercentage} <= 100`
		),
		// Owner uniqueness is per (party, ownership class): a party may hold one row per class,
		// including one class-less row. Partial unique indexes cannot be NULLS NOT DISTINCT
		// constraints, so a null class coalesces to a sentinel uuid to make nulls compare equal
		// (mirrors company_ownership_classes_identity_uq's nullsNotDistinct convention).
		uniqueIndex("company_parties_dedup_company")
			.on(
				table.companyId,
				table.role,
				table.partyCompanyId,
				sql`COALESCE(${table.ownershipClassId}, '00000000-0000-0000-0000-000000000000'::uuid)`
			)
			.where(sql`${table.partyCompanyId} IS NOT NULL`),
		uniqueIndex("company_parties_dedup_contact")
			.on(
				table.companyId,
				table.role,
				table.partyContactId,
				sql`COALESCE(${table.ownershipClassId}, '00000000-0000-0000-0000-000000000000'::uuid)`
			)
			.where(sql`${table.partyContactId} IS NOT NULL`),
		index("company_parties_company_id_role_idx").on(table.companyId, table.role),
		index("company_parties_ownership_class_id_company_id_role_idx").on(
			table.ownershipClassId,
			table.companyId,
			table.role
		),
		index("company_parties_party_company_id_idx").on(table.partyCompanyId),
		index("company_parties_party_contact_id_idx").on(table.partyContactId)
	]
);
